import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import type { DateTimeValue } from '@elfsight/configurator-contracts';
import { useInterval } from '@elfsight/lib-utils';
import { Tooltip } from '@elfsight/widget-modal';
import { useRerender } from '@elfsight/widget-utils';
import type { DurationUnit } from 'luxon';
import { format, formatFull } from './formatDateTime';
import { parse } from './parseDateTime';
import type { FormatProps } from './types';

type DateTimeProps = {
  value: DateTimeValue<'datetime'> | Date | number | null;
  format?: FormatProps['format'];
  locale?: FormatProps['locale'];
  stripSameYear?: FormatProps['stripSameYear'];
  relative?: FormatProps['relative'];
  recentLabel?: string;
  relativeActive?: boolean;
  relativeAlways?: boolean;
  relativeIntervalUnit?: DurationUnit;
  relativeIntervalValue?: number;
  relativeObserve?: boolean;
  tooltipBackgroundColor?: string;
  tooltipFontColor?: string;
  className?: string;
  withTooltip?: boolean;
};

export const DateTime = forwardRef<HTMLDivElement, DateTimeProps>(
  (
    {
      value,
      locale = 'auto',
      format: dateFormat = 'DATE_FULL',
      stripSameYear = true,
      tooltipBackgroundColor,
      tooltipFontColor,
      relative = {},
      recentLabel,
      relativeActive = true,
      relativeAlways = false,
      relativeIntervalUnit = 'DAYS' as DurationUnit,
      relativeIntervalValue = 7,
      relativeObserve = true,
      withTooltip = true,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const dateTime = useMemo(() => parse(value), [value]);

    const { formattedDate, relativeObserverTimeout } = format(dateTime, {
      format: dateFormat,
      locale,
      stripSameYear,
      recentLabel,
      relative: getRelativeProps({
        relative,
        relativeActive,
        relativeAlways,
        relativeObserve,
        relativeIntervalUnit,
        relativeIntervalValue
      })
    });
    const absoluteDate = formatFull(dateTime, locale);
    const forceUpdate = useRerender();

    useInterval(forceUpdate, relativeObserverTimeout);

    if (withTooltip) {
      return (
        <Tooltip
          title={absoluteDate}
          background={tooltipBackgroundColor}
          fontColor={tooltipFontColor}
        >
          <Time ref={forwardingRef} {...forwardingProps}>
            {formattedDate}
          </Time>
        </Tooltip>
      );
    }

    return (
      <Time ref={forwardingRef} {...forwardingProps}>
        {formattedDate}
      </Time>
    );
  }
);

function getRelativeProps({
  relative,
  relativeActive,
  relativeAlways,
  relativeObserve,
  relativeIntervalUnit,
  relativeIntervalValue
}: Partial<DateTimeProps>) {
  return {
    ...relative,
    active: relativeActive,
    always: relativeAlways,
    observe: relativeObserve,
    interval: {
      unit: relativeIntervalUnit,
      value: relativeIntervalValue
    }
  };
}

const Time = styled.div``;
